<template>
  <div class="banner d-flex d-column p-3 p-lg-5">
    <div class="banner__background" v-bind:style="backgroundStyle">
      <video v-if="cover.type === 'video'" class="banner__video" autoplay loop muted plays-inline :poster="toBase(cover.fallback)">
        <source :src="toBase(cover.source)" type="video/mp4">
      </video>
      <div v-if="cover.type !== 'color'" class="banner__overlay">

      </div>
    </div>
    <div class="banner__content my-auto">
      <h1 class="mb-2">
        {{ firstname }}
        <span class="text-primary">{{ lastname }}</span>
      </h1>
      <div class="subheading mb-5">
        {{ role }}
        <a
          :href="'mailto:' + email"
        >{{ email }}</a>
      </div>
      <div class="social-icons py-5">
        <a
          v-if="social.scopus"
          :href="'https://www.scopus.com/authid/detail.uri?authorId=' + social.scopus"
          target="_blank"
          title="Scopus">
          <img :src="toBase('/static/img/scopus_icon.png')" width="48" height="48"/>
        </a>
        <a
          v-if="social.orcid"
          :href="'https://orcid.org/' + social.orcid"
          target="_blank"
          title="OrcID">
          <img :src="toBase('/static/img/orcid_icon.png')" width="48" height="48"/>
        </a>
        <a
          v-if="social.dblp"
          :href="'https://dblp.uni-trier.de/pers/hd/f/' + social.dblp"
          target="_blank"
          title="DBLP"
          style="background-color: #FFFFFF;">
          <img :src="toBase('/static/img/dblp_icon.png')" width="26" height="34"
            style="vertical-align: text-top !important;"/>
        </a>
        <a
          v-if="social.googlescholar"
          :href="'https://scholar.google.pt/citations?user=' + social.googlescholar"
          target="_blank"
          title="Google Scholar"
          style="background-color: #4285f4;">
          <icon-googlescholar width="24" height="24"/>
        </a>
        <a
          v-if="social.facebook"
          :href="'https://www.facebook.com/' + social.facebook"
          target="_blank"
          title="Facebook">
          <font-awesome-icon prefix :icon="['fab', 'facebook-f']"/>
        </a>
        <a
          v-if="social.twitter"
          :href="'https://twitter.com/' + social.twitter"
          target="_blank"
          title="Twitter">
          <font-awesome-icon prefix :icon="['fab', 'twitter']"/>
        </a>
        <a
          v-if="social.linkedin"
          :href="'https://linkedin.com/in/' + social.linkedin"
          target="_blank"
          title="LinkedIn"
          style="background-color: #005680;">
          <font-awesome-icon prefix :icon="['fab', 'linkedin-in']"/>
        </a>
        <a
          v-if="social.github"
          :href="'https://github.com/' + social.github"
          target="_blank"
          title="Github">
          <font-awesome-icon prefix :icon="['fab', 'github']"/>
        </a>
        <a
          v-if="social.researchgate"
          :href="'https://www.researchgate.net/profile/' + social.researchgate"
          target="_blank"
          title="ResearchGate"
          style="background-color: #00D0B0;">
          <icon-researchgate width="24" height="24"/>
        </a>
        <a
          :href="$router.resolve({ name: 'print-resume' }).href"
          target="_blank"
          title="Resume"
          style="background-color: #000000;" >
          <font-awesome-icon prefix :icon="['fas', 'address-card']"/>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import IconResearchGate from '@/icons/typography/IconResearchGate'
import IconOrcidId from '@/icons/typography/IconOrcidId'
import IconGoogleScholar from '@/icons/typography/IconGoogleScholar'

export default {

  props: {
    cover: {
      type: Object,
      default: () => ({ type: 'color', source: '#ffffff' })
    },
    firstname: {
      type: String,
      default: 'John'
    },
    lastname: {
      type: String,
      default: 'Doe'
    },
    email: {
      type: String,
      default: 'johndoe@nomail.com'
    },
    role: {
      type: String,
      default: 'Developer'
    },
    social: {
      type: Object,
      default: () => {}
    }
  },

  components: {
    'icon-orcid-id': IconOrcidId,
    'icon-researchgate': IconResearchGate,
    'icon-googlescholar': IconGoogleScholar
  },

  data () {
    return {
    }
  },

  computed: {
    ...mapGetters(['toBase']),

    backgroundStyle () {
      if (this.cover.type === 'color') {
        return { 'background-color': this.cover.source }
      } else if (this.cover.type === 'image') {
        return { 'background-image': 'url(' + this.toBase(this.cover.source) + ')' }
      } else if (this.cover.type === 'video') {
        return { 'background-image': 'url(' + this.toBase(this.cover.fallback) + ')' }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.banner {
  position: relative;
  width: 100%;
  padding-top: 5rem !important;
  padding-bottom: 5rem !important;

  @media (min-width: 992px) {
    min-height: 100vh;
  }

  @media (max-width: 992px) {
    position: relative;
    min-height: auto;
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }

  &__background {
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: -99;
    overflow: hidden;
    top: 0; right: 0; bottom: 0; left: 0;
    -webkit-background-size: cover;
    -moz-background-size: cover;
    -o-background-size: cover;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
  }

  &__video {
    position: fixed;
    top: 50%;
    left: 50%;
    width: auto;
    height: auto;
    min-width: 100%;
    min-height: 100vh;
    -webkit-transform: translate(-50%, -50%);
    -moz-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    z-index: -3;
    pointer-events: none;

    @media (max-width: 1200px) {
      display: none;
    }
  }

  &__overlay {
    position: absolute;
    z-index: -2;
    top: 0; right: 0; bottom: 0; left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba($light-shades, 0.65);
    pointer-events: none;
  }

  &__content {
    z-index: 1;
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Banner.vue